import classNames from 'classnames';
import { useId } from 'react';

import LoadingIcon from '../icons/LoadingIcon';

import styles from '../../styles/fields/radios-field.module.css';

interface ButtonsFieldProps {
    type: string;
    name: string;
    errors?: string[] | null;
    value?: string | string[] | null;
    options: FieldOption[];
    multiple?: boolean;
    loading?: boolean;
    className?: string;
    onChange: (value: string | string[] | null) => void;
}

function ButtonsField({
    type,
    name,
    value,
    errors = null,
    options,
    multiple = false,
    loading = false,
    className,
    onChange = null,
}: ButtonsFieldProps) {
    const id = useId();
    const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { checked, value: checkedValue } = event.currentTarget;
        let newValue = null;
        if (multiple) {
            newValue = checked
                ? [...(value || []), checkedValue]
                : (value || []).filter((v) => v !== checkedValue);
        } else {
            newValue = checked ? checkedValue : null;
        }
        if (onChange !== null) {
            onChange(newValue !== null && newValue.length > 0 ? newValue : null);
        }
    };

    const hasErrors = errors !== null && errors.length > 0;

    return (
        <div
            className={classNames([styles.container, { [styles.hasErrors]: hasErrors }, className])}
            data-theme={type}
        >
            <div className={styles.options}>
                {options.map(({ value: optionValue, label: optionLabel }, index) => {
                    const isAnswer = multiple
                        ? (value || []).indexOf(optionValue) !== -1
                        : value === optionValue;
                    return (
                        <div
                            key={`option-${optionValue}`}
                            className={classNames([
                                styles.option,
                                {
                                    [styles.loading]: loading && isAnswer,
                                    [styles.notSelected]: value !== null && !isAnswer,
                                    [styles.selected]: isAnswer,
                                },
                            ])}
                        >
                            <input
                                type={multiple ? 'checkbox' : 'radio'}
                                id={`${id}-${optionValue}`}
                                name={name}
                                value={optionValue}
                                checked={isAnswer}
                                onChange={onInputChange}
                                className={styles.input}
                            />
                            <label htmlFor={`${id}-${optionValue}`} className={styles.button}>
                                <span className={styles.prefix}>
                                    <span className={styles.index}>{index + 1}</span>
                                    <LoadingIcon className={styles.loadingIcon} />
                                </span>
                                <span className={styles.label}>{optionLabel}</span>
                            </label>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default ButtonsField;
